/**
 * Copyright (C) 2026 alaraajavamma <aki@urheiluaki.fi>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 *
 * SPDX-License-Identifier: GPL-3.0-or-later
 */

import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Plus,
  Trash2,
  Printer,
  RefreshCw,
  Check,
  LayoutGrid,
  Sparkles,
  Info,
  X
} from 'lucide-react';
import { PriceTagItem, LayoutConfig, TagSizePreset } from './types';
import { DEFAULT_TAG_ITEMS } from './presets';
import { PriceTag } from './components/PriceTag';
import { IntersportLogo } from './components/IntersportLogo';

// Helper to determine active color of the campaign badge
const getActiveBadgeBg = (item: PriceTagItem | undefined): string => {
  if (!item) return '';
  if (item.extraBadgeBg) return item.extraBadgeBg;
  if (!item.extraBadge) return '';

  const text = item.extraBadge.toLowerCase();
  if (text.includes('tarjous') || text.includes('ale') || text.includes('-') || text.includes('kampanja') || text.includes('%')) {
    return '#E30613'; // Red
  } else if (text.includes('kanta-asiakas') || text.includes('plussa') || text.includes('club') || text.includes('jäsen') || text.includes('etu')) {
    return '#005aa3'; // Blue
  } else if (text.includes('uutuus')) {
    return '#10b981'; // Green
  }
  return '#18181b'; // Dark Zinc
};

export default function App() {
  // Application local state
  const [items, setItems] = useState<PriceTagItem[]>([DEFAULT_TAG_ITEMS[0]]);
  const [selectedItemId, setSelectedItemId] = useState<string>(DEFAULT_TAG_ITEMS[0]?.id || '');
  const [showNotification, setShowNotification] = useState<string | null>(null);
  const [zoomPercent, setZoomPercent] = useState<number>(60);

  // Layout presentation configuration
  const [config, setConfig] = useState<LayoutConfig>({
    preset: 'A6_PORTRAIT', // Default to A6 Portrait (4 tags on page)
    showCutLines: true,
    showBrand: true,
    showCategory: true,
    showDetails: true,
    showBadge: true
  });

  // active item helper
  const selectedItem = useMemo(() => {
    return items.find(item => item.id === selectedItemId);
  }, [items, selectedItemId]);

  // Notifications helper
  const triggerNotification = (msg: string) => {
    setShowNotification(msg);
    setTimeout(() => setShowNotification(null), 3000);
  };

  // Preset detail labels and sizes info with orientation spec
  const presetLabels: Record<TagSizePreset, { title: string; desc: string; count: number; orientation: 'portrait' | 'landscape' }> = {
    A4_PORTRAIT: { title: 'A4 Pysty', desc: '1 kpl per sivu (210 x 297 mm)', count: 1, orientation: 'portrait' },
    A5_PORTRAIT: { title: 'A5 Pysty', desc: '2 kpl per sivu (148.5 x 210 mm)', count: 2, orientation: 'landscape' },
    A6_PORTRAIT: { title: 'A6 Pysty', desc: '4 kpl per sivu (105 x 148.5 mm)', count: 4, orientation: 'portrait' },
    A6_LANDSCAPE: { title: 'A6 Vaaka', desc: '4 kpl per sivu (148.5 x 105 mm)', count: 4, orientation: 'landscape' },
    SHOE_STRIP: { title: 'Kenkäluiska', desc: '4 kpl per sivu (249 x 49 mm)', count: 4, orientation: 'landscape' }
  };

  // Change individual key in any product model directly
  const handleItemChangeDirect = (itemId: string, field: keyof PriceTagItem, value: any) => {
    setItems(prev =>
      prev.map(it => (it.id === itemId ? { ...it, [field]: value } : it))
    );
  };

  // Change individual key in active product model
  const handleItemChange = (field: keyof PriceTagItem, value: any) => {
    if (!selectedItemId) return;
    handleItemChangeDirect(selectedItemId, field, value);
  };

  // Add new blank layout item
  const handleAddItem = () => {
    const newId = Date.now().toString();
    const newItem: PriceTagItem = {
      id: newId,
      title: 'Uusi tuote',
      brand: 'Merkki',
      category: 'Kategoria',
      priceNormal: '99,00',
      priceSale: '',
      sizeRange: 'S-XL',
      colorInfo: 'Musta',
      extraBadge: '',
      productCode: '100-00000',
      description: '',
      quantity: 1
    };
    setItems(prev => [newItem, ...prev]);
    setSelectedItemId(newId);
    triggerNotification('Uusi lapputuote lisätty listan alkuun!');
  };

  // Remove active tag item from selection list
  const handleDeleteItem = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (items.length <= 1) {
      triggerNotification('Yhteenveto: Listalla on oltava vähintään yksi tuote!');
      return;
    }
    const idx = items.findIndex(it => it.id === id);
    const newItems = items.filter(it => it.id !== id);
    setItems(newItems);

    if (selectedItemId === id) {
      // Pick next available
      const nextSelect = newItems[Math.max(0, idx - 1)];
      setSelectedItemId(nextSelect ? nextSelect.id : '');
    }
    triggerNotification('Tuote poistettu hintalapuista');
  };

  // Multiply items based on their individual print quantities
  const expandedItems = useMemo(() => {
    const list: PriceTagItem[] = [];
    items.forEach(item => {
      const qty = item.quantity || 1;
      for (let i = 0; i < qty; i++) {
        list.push(item);
      }
    });
    return list;
  }, [items]);

  // Sort chunks to fit physical A4 paper grid
  const chunkedPages = useMemo(() => {
    const countPerPage = presetLabels[config.preset].count;
    const pages: (PriceTagItem | null)[][] = [];
    
    for (let i = 0; i < expandedItems.length; i += countPerPage) {
      const chunk = expandedItems.slice(i, i + countPerPage);
      const page: (PriceTagItem | null)[] = [...chunk];
      // Pad empty slots with null to preserve physical cut bounds
      while (page.length < countPerPage) {
        page.push(null);
      }
      pages.push(page);
    }
    return pages;
  }, [expandedItems, config.preset]);

  // Simple direct system print trigger
  const handlePrint = () => {
    try {
      window.print();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-slate-50 text-slate-800 font-sans selection:bg-[#005aa3] selection:text-white print:h-auto print:overflow-visible">
      {/* 1. Brand Header bar (Hidden in print) */}
      <header className="h-16 bg-white border-b border-slate-200 px-6 flex items-center justify-between gap-4 no-print shrink-0 shadow-xs z-10">
        <div className="flex items-center gap-3">
          <IntersportLogo variant="color" showText={false} className="scale-90 origin-left" />
          <div className="h-4 w-[1px] bg-slate-200 mx-1.5 hidden sm:block"></div>
          <h1 className="text-slate-400 font-bold uppercase tracking-wider text-[10px] hidden sm:block">
            Hintalappustudio
          </h1>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handlePrint}
            className="px-5 py-2 text-xs font-bold text-white bg-[#f37021] hover:bg-[#e05f10] shadow-sm rounded flex items-center gap-1.5 transition-colors cursor-pointer"
          >
            <Printer className="h-3.5 w-3.5" />
            <span>TULOSTA LAPUT</span>
          </button>
        </div>
      </header>

      {/* Main workspace container (Left: form & list. Right: Print Sheet Preview) */}
      <main className="flex-1 flex flex-col md:flex-row items-stretch overflow-hidden">
        
        {/* LEFT WORKSPACE SIDEBAR - Controls & List (Hidden in print) */}
        <section className="w-full md:w-[380px] lg:w-[440px] xl:w-[460px] bg-white border-r border-slate-200 overflow-y-auto max-h-full flex flex-col shrink-0 no-print">
          
          {/* Section A: Selection List */}
          <div className="p-4 border-b border-slate-100 bg-slate-50">
            <div className="flex items-center justify-start mb-3">
              <button
                onClick={handleAddItem}
                className="px-3 py-1.5 text-xs font-bold text-white bg-[#005aa3] hover:bg-[#004b87] rounded flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                <Plus className="h-3.5 w-3.5" />
                <span>LISÄÄ UUSI</span>
              </button>
            </div>

            {/* Scrollable list box */}
            <div className="max-h-[300px] overflow-y-auto pr-1 flex flex-col gap-1.5 custom-scrollbar">
              {items.length === 0 ? (
                <div className="text-center py-6 text-slate-400 text-xs font-mono">
                  Ei tuotteita...
                </div>
              ) : (
                items.map(item => {
                  const isActive = item.id === selectedItemId;
                  return (
                    <div
                      key={item.id}
                      onClick={() => setSelectedItemId(item.id)}
                      className={`group p-2 rounded transition-all cursor-pointer flex items-center justify-between gap-3 text-left border
                        ${isActive 
                          ? 'bg-slate-50 border-[#005aa3] ring-1 ring-[#005aa3] shadow-xs' 
                          : 'bg-white border-slate-200 hover:border-slate-300 text-slate-700'}`}
                    >
                      <div className="truncate flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-black px-1.5 py-0.5 rounded uppercase tracking-wider
                            ${isActive ? 'bg-blue-100 text-[#005aa3]' : 'bg-slate-100 text-slate-500'}`}>
                            {item.brand || 'Ei brändiä'}
                          </span>
                          {item.extraBadge && (
                            <span className="text-[9px] font-black bg-red-600 text-white px-1 rounded-sm uppercase tracking-wide">
                              {item.extraBadge}
                            </span>
                          )}
                        </div>
                        <h4 className="text-xs font-bold text-slate-900 truncate mt-1">
                          {item.title || 'Nimetön tuote'}
                        </h4>
                        <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-slate-500 font-mono">
                          {item.priceNormal && item.priceSale ? (
                            <>
                              <span className="line-through opacity-60 mr-1">{item.priceNormal} €</span>
                              <span className="text-red-600 font-bold">{item.priceSale} €</span>
                            </>
                          ) : item.priceSale ? (
                            <span className="text-red-600 font-bold">{item.priceSale} €</span>
                          ) : (
                            <span>{item.priceNormal || '0'} €</span>
                          )}
                        </div>
                      </div>

                      {/* Action buttons with Print Quantity controls */}
                      <div className="flex items-center gap-1.5 shrink-0" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center bg-slate-150/80 border border-slate-200/80 rounded p-0.5 text-xs font-bold select-none">
                          <button
                            type="button"
                            disabled={(item.quantity || 1) <= 1}
                            onClick={() => {
                              const currentQty = item.quantity || 1;
                              if (currentQty > 1) {
                                handleItemChangeDirect(item.id, 'quantity', currentQty - 1);
                              }
                            }}
                            className="w-5 h-5 flex items-center justify-center rounded text-slate-700 hover:bg-slate-200 hover:text-slate-950 transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-700 cursor-pointer disabled:cursor-not-allowed text-xs font-black"
                            title="Vähennä määrää"
                          >
                            -
                          </button>
                          <span className="w-5.5 text-center font-mono text-[11px] text-slate-800 leading-none font-black">
                            {item.quantity || 1}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              const currentQty = item.quantity || 1;
                              handleItemChangeDirect(item.id, 'quantity', currentQty + 1);
                            }}
                            className="w-5 h-5 flex items-center justify-center rounded text-slate-700 hover:bg-slate-200 hover:text-slate-950 transition-colors cursor-pointer text-xs font-black"
                            title="Lisää määrää"
                          >
                            +
                          </button>
                        </div>

                        <button
                          onClick={e => handleDeleteItem(item.id, e)}
                          title="Poista tuote"
                          className="p-1 rounded text-slate-400 hover:text-red-500 hover:bg-slate-100 transition-colors cursor-pointer"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>


          </div>

          {/* Section B: Active Item Editor (Super easy interface) */}
          <div className="p-5 border-b border-slate-100 bg-white flex-1">
            <h2 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-4 flex items-center gap-1.5">
              <span>Muokkaa sisältöä</span>
            </h2>

            {selectedItem ? (
              <div className="space-y-4">
                {/* Brand & Price group */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-450 uppercase tracking-wider mb-1">
                      Tuotemerkki
                    </label>
                    <input
                      type="text"
                      value={selectedItem.brand}
                      onChange={e => handleItemChange('brand', e.target.value)}
                      className="w-full bg-white border border-slate-200 text-xs py-1.5 px-3 rounded text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#005aa3]/10 focus:border-[#005aa3] font-medium transition-all"
                      placeholder="esim. Nike"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-450 uppercase tracking-wider mb-1">
                      Kategoria
                    </label>
                    <input
                      type="text"
                      value={selectedItem.category}
                      onChange={e => handleItemChange('category', e.target.value)}
                      className="w-full bg-white border border-slate-200 text-xs py-1.5 px-3 rounded text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#005aa3]/10 focus:border-[#005aa3] font-medium transition-all"
                      placeholder="esim. Juoksukenkä"
                    />
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-[10px] font-bold text-slate-450 uppercase tracking-wider mb-1">
                    Tuotteen nimi
                  </label>
                  <input
                    type="text"
                    value={selectedItem.title}
                    onChange={e => handleItemChange('title', e.target.value)}
                    className="w-full bg-white border border-slate-200 text-xs py-2 px-3 rounded text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#005aa3]/10 focus:border-[#005aa3] font-bold transition-all"
                    placeholder="esim. Air Zoom Pegasus 40"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-[10px] font-bold text-slate-450 uppercase tracking-wider mb-1">
                    Tuotteen kuvaus
                  </label>
                  <textarea
                    rows={2}
                    value={selectedItem.description || ''}
                    onChange={e => handleItemChange('description', e.target.value)}
                    className="w-full bg-white border border-slate-200 text-xs py-1.5 px-3 rounded text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#005aa3]/10 focus:border-[#005aa3] transition-all font-sans"
                    placeholder="Esim. Kevyt ja hengittävä juoksukenkä loistavalla vaimennuksella."
                  />
                </div>

                {/* Prices section with design colors */}
                <div className="bg-slate-50 p-3 rounded border border-slate-200 space-y-3">
                  {/* Mode Selector Toggle */}
                  <div className="flex items-center justify-between border-b border-slate-200/60 pb-2">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                      Alennuksen tyyppi
                    </span>
                    <div className="flex bg-slate-200/80 p-0.5 rounded-md text-[9px] font-black uppercase">
                      <button
                        type="button"
                        onClick={() => handleItemChange('isPercentage', false)}
                        className={`px-3 py-1 rounded transition-all cursor-pointer ${
                          !selectedItem.isPercentage
                            ? 'bg-white text-slate-900 shadow-xs'
                            : 'text-slate-500 hover:text-slate-800'
                        }`}
                      >
                        Hinta (€)
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          handleItemChange('isPercentage', true);
                          handleItemChange('priceNormal', '');
                        }}
                        className={`px-3 py-1 rounded transition-all cursor-pointer ${
                          selectedItem.isPercentage
                            ? 'bg-[#005aa3] text-white shadow-xs'
                            : 'text-slate-550 hover:text-slate-800'
                        }`}
                      >
                        Prosentti (%)
                      </button>
                    </div>
                  </div>

                  {selectedItem.isPercentage ? (
                    <div>
                      <label className="block text-[10px] font-bold text-[#005aa3] uppercase tracking-wider mb-1">
                        Prosenttialennus
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={selectedItem.priceSale}
                          onChange={e => handleItemChange('priceSale', e.target.value)}
                          className="w-full bg-white border border-slate-200 text-sm font-black py-1.5 pl-3 pr-7 rounded focus:outline-none focus:ring-2 focus:ring-[#005aa3]/10 focus:border-[#005aa3] text-[#005aa3]"
                          placeholder="esim. 20"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 font-bold text-[#005aa3] text-xs">%</span>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-450 uppercase tracking-wider mb-1">
                          Normaalihinta
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={selectedItem.priceNormal}
                            onChange={e => handleItemChange('priceNormal', e.target.value)}
                            className="w-full bg-white border border-slate-200 text-slate-900 text-sm font-black py-1.5 pl-3 pr-7 rounded focus:outline-none focus:ring-2 focus:ring-[#005aa3]/10 focus:border-[#005aa3]"
                            placeholder="149"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">€</span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-[10px] font-bold text-red-600 uppercase tracking-wider mb-1">
                          ERIKOISHINTA
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={selectedItem.priceSale}
                            onChange={e => handleItemChange('priceSale', e.target.value)}
                            className="w-full bg-white border border-slate-200 text-red-600 text-sm font-black py-1.5 pl-4 pr-7 rounded focus:outline-none focus:ring-2 focus:ring-red-100 focus:border-red-500"
                            placeholder="esim. 99,90"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-400 font-bold text-xs">€</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Kampanja / Huomioteksti */}
                <div>
                  <label className="block text-[10px] font-bold text-slate-450 uppercase tracking-wider mb-1">
                    Kampanja / Huomioteksti
                  </label>
                  <input
                    type="text"
                    value={selectedItem.extraBadge}
                    onChange={e => handleItemChange('extraBadge', e.target.value)}
                    className="w-full bg-white border border-slate-200 text-xs py-1.5 px-3 rounded text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#005aa3]/10 focus:border-[#005aa3] transition-all"
                    placeholder="Esim. Tarjous, Kanta-asiakasetu, -20%"
                  />
                </div>

                {/* Campaign Badge Background Color Picker */}
                <div className="bg-slate-50 p-3 rounded border border-slate-200 space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                      Huomiotekstin taustaväri
                    </label>
                    <span className="text-[9px] font-mono text-slate-500 font-bold uppercase">
                      {getActiveBadgeBg(selectedItem) || 'Ei valittuna'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {/* Native color picker input */}
                    <div className="relative flex items-center gap-1.5 bg-white border border-slate-200 rounded p-1 pr-2 shadow-2xs">
                      <input
                        type="color"
                        value={getActiveBadgeBg(selectedItem) || '#E30613'}
                        onChange={e => handleItemChange('extraBadgeBg', e.target.value)}
                        className="w-6 h-6 p-0 border-0 rounded cursor-pointer shrink-0"
                        title="Valitse vapaa väri"
                      />
                      <span className="text-[9px] font-bold text-slate-600">Oma väri</span>
                    </div>

                    {/* Pre-set color chips */}
                    {[
                      { hex: '#E30613', label: 'Punainen', bg: 'bg-[#E30613]' },
                      { hex: '#005aa3', label: 'Sininen', bg: 'bg-[#005aa3]' },
                      { hex: '#00133A', label: 'Tummansininen', bg: 'bg-[#00133A]' },
                      { hex: '#10b981', label: 'Vihreä', bg: 'bg-[#10b981]' },
                      { hex: '#fabb15', label: 'Keltainen', bg: 'bg-[#fabb15]' },
                      { hex: '#E25211', label: 'Oranssi', bg: 'bg-[#E25211]' },
                      { hex: '#e11d48', label: 'Vaaleanpunainen', bg: 'bg-[#e11d48]' },
                      { hex: '#7c3aed', label: 'Violetti', bg: 'bg-[#7c3aed]' },
                      { hex: '#18181b', label: 'Kenkätumma', bg: 'bg-[#18181b]' },
                    ].map(chip => {
                      const activeBg = getActiveBadgeBg(selectedItem);
                      const isSelected = activeBg && activeBg.toLowerCase() === chip.hex.toLowerCase();
                      return (
                        <button
                          key={chip.hex}
                          type="button"
                          onClick={() => handleItemChange('extraBadgeBg', chip.hex)}
                          className={`w-6 h-6 rounded-full border border-slate-200 hover:scale-110 active:scale-95 transition-transform ${chip.bg} shadow-xs relative cursor-pointer`}
                          title={chip.label}
                        >
                          {isSelected && (
                            <span className="absolute inset-0 flex items-center justify-center text-white font-black text-[10px] leading-none">✓</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-10 text-slate-400 text-xs">
                Valitse tuote listalta tai paina "Lisää uusi" aloittaaksesi muokkauksen.
              </div>
            )}
          </div>

          {/* Section C: Paper print options layout */}
          <div className="p-4 border-t border-slate-200 bg-slate-50">
            <h2 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
              Lapputyypit &amp; asettelu
            </h2>

            {/* Layout options preset selector */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              {(Object.keys(presetLabels) as TagSizePreset[]).map(presetKey => {
                const labelInfo = presetLabels[presetKey];
                const isSelected = config.preset === presetKey;
                return (
                  <button
                    key={presetKey}
                    onClick={() => setConfig(prev => ({ ...prev, preset: presetKey }))}
                    className={`p-2.5 rounded border text-left flex flex-col justify-between transition-all cursor-pointer
                      ${isSelected 
                        ? 'bg-white border-2 border-[#005aa3] shadow-sm text-[#005aa3] font-bold' 
                        : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className="text-xs font-bold">{labelInfo.title}</span>
                      {isSelected && <Check className="h-3.5 w-3.5 text-[#005aa3]" />}
                    </div>
                    <span className="text-[10px] text-slate-400 font-medium tracking-tight mt-1">{labelInfo.desc}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* RIGHT PREVIEW WORKSPACE - Interactive Paper Canvas */}
        <section className="flex-1 bg-slate-100 overflow-y-auto p-4 md:p-8 flex flex-col items-center justify-start gap-6 max-h-full">
          {/* Visual indicator bar (Hidden in print) */}
          <div className="w-full max-w-4xl no-print bg-white border border-slate-200 text-slate-600 px-4 py-2.5 rounded shadow-xs text-xs flex flex-col md:flex-row items-center justify-between gap-3 shrink-0">
            <span className="flex items-center gap-2 font-bold uppercase tracking-wider text-[10px] text-slate-550">
              <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              <span>A4 Lasketut sivut tulostusta varten ({chunkedPages.length} sivu{chunkedPages.length !== 1 ? 'ä' : ''})</span>
            </span>
            <div className="flex items-center gap-3 shrink-0">
              {/* Zoom Scale Controls */}
              <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded px-2 py-1 select-none text-[11px]">
                <span className="text-slate-400 font-bold uppercase text-[9px] mr-1">Zoomi:</span>
                <button
                  onClick={() => setZoomPercent(prev => Math.max(30, prev - 5))}
                  className="w-5 h-5 rounded hover:bg-slate-200 text-slate-600 flex items-center justify-center font-extrabold transition-colors cursor-pointer text-xs"
                  title="Pienennä esikatselua"
                >
                  -
                </button>
                <span className="font-mono font-bold text-slate-700 w-9 text-center text-xs">{zoomPercent}%</span>
                <button
                  onClick={() => setZoomPercent(prev => Math.min(120, prev + 5))}
                  className="w-5 h-5 rounded hover:bg-slate-200 text-slate-600 flex items-center justify-center font-extrabold transition-colors cursor-pointer text-xs"
                  title="Suurenna esikatselua"
                >
                  +
                </button>
                <div className="h-3 w-[1px] bg-slate-200 mx-1"></div>
                <button
                  onClick={() => setZoomPercent(60)}
                  className="text-[10px] text-[#005aa3] hover:text-[#004b87] font-bold"
                  title="Palauta oletussuhde"
                >
                  60% (Oletus)
                </button>
                <button
                  onClick={() => setZoomPercent(50)}
                  className="text-[10px] text-slate-500 hover:text-slate-800 font-bold ml-1"
                  title="Aseta pieneksi jotta mahtuu"
                >
                  50%
                </button>
              </div>
              <div className="flex items-center gap-2 text-[10px] text-slate-400 font-mono">
                <span className="bg-[#005aa3] text-white text-[9px] font-black px-1.5 py-1 rounded leading-none uppercase">ESIKATSELU</span>
              </div>
            </div>
          </div>

          {/* Printable Page Loop */}
          <div className="flex flex-col gap-6 w-full items-center printable-pages-flow">
            {chunkedPages.map((page, pageIdx) => {
              const labelInfo = presetLabels[config.preset];
              const isLandscape = labelInfo.orientation === 'landscape';
              const scale = zoomPercent / 100;

              // Determine grid columns and spacings on screen
              let gridClasses = "grid bg-white ";
              if (config.preset === 'A4_PORTRAIT') {
                gridClasses += "grid-cols-1 grid-rows-1 justify-items-center content-center";
              } else if (config.preset === 'A5_PORTRAIT') {
                // Two A5 portraits (each 148.5x210) side by side on A4 Landscape (297x210)
                gridClasses += "grid-cols-2 grid-rows-1 gap-0 w-[297mm] h-[210mm] justify-items-center content-center";
              } else if (config.preset === 'A6_PORTRAIT') {
                // 2x2 grid of A6 portrait on A4 Portrait (210x297)
                gridClasses += "grid-cols-2 grid-rows-2 gap-0 w-[210mm] h-[297mm] justify-items-center content-center";
              } else if (config.preset === 'A6_LANDSCAPE') {
                // 2x2 grid of A6 landscape on A4 Landscape (297x210)
                gridClasses += "grid-cols-2 grid-rows-2 gap-0 w-[297mm] h-[210mm] justify-items-center content-center";
              } else if (config.preset === 'SHOE_STRIP') {
                // 4 shoe shelf strips stacked vertically, strictly top-aligned matching the physical 7mm top margin
                gridClasses = "flex flex-col items-center justify-start pt-[7mm] gap-0 w-[297mm] h-[210mm] bg-white";
              }

              return (
                <div
                  key={pageIdx}
                  className="preview-page-wrapper relative print:static shrink-0"
                  style={{
                    width: `calc(${isLandscape ? '297mm' : '210mm'} * ${scale})`,
                    height: `calc(${isLandscape ? '210mm' : '297mm'} * ${scale})`,
                  }}
                >
                  <div 
                    className={`print-page-cnt absolute top-0 left-0 print:static shadow-xl shadow-slate-300/40 flex flex-col items-center justify-center bg-white border border-slate-300 origin-top-left shrink-0 overflow-hidden ring-8 ring-slate-200/20
                      ${isLandscape ? 'print-page-landscape' : 'print-page-portrait'}`}
                    style={{
                      width: isLandscape ? '297mm' : '210mm',
                      height: isLandscape ? '210mm' : '297mm', 
                      transform: `scale(${scale})`,
                      transformOrigin: 'top left',
                    }}
                  >
                    {/* Subtle Screen Badge (Hidden in print) */}
                    <div className="absolute top-3 right-3 no-print bg-[#005aa3] text-white text-[9px] font-black px-2 py-0.5 rounded shadow-sm z-20 uppercase tracking-widest">
                      Sivu {pageIdx + 1} ({isLandscape ? 'Vaaka A4' : 'Pysty A4'})
                    </div>

                    {/* The A4 Sheet Content */}
                    <div className={`${gridClasses} w-full h-full align-middle justify-center`}>
                      {page.map((item, itemIdx) => {
                        const isShoeStrip = config.preset === 'SHOE_STRIP';
                        const itemSpacingClass = isShoeStrip && itemIdx > 0 ? '-mt-[1px]' : '';

                        if (!item) {
                          // Empty slot grid placeholder
                          let emptySizeClass = "";
                          if (config.preset === 'A4_PORTRAIT') {
                            emptySizeClass = "w-[210mm] h-[297mm]";
                          } else if (config.preset === 'A5_PORTRAIT') {
                            emptySizeClass = "w-[148.5mm] h-[210mm]";
                          } else if (config.preset === 'A6_PORTRAIT') {
                            emptySizeClass = "w-[105mm] h-[148.5mm]";
                          } else if (config.preset === 'A6_LANDSCAPE') {
                            emptySizeClass = "w-[148.5mm] h-[105mm]";
                          } else if (config.preset === 'SHOE_STRIP') {
                            emptySizeClass = "w-[249mm] h-[49mm]";
                          }

                          return (
                            <div 
                              key={`empty-${itemIdx}`} 
                              className={`bg-slate-50 border border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-400 font-mono text-center print:border-none print:bg-transparent ${emptySizeClass} ${itemSpacingClass} empty-slot-marker`}
                            >
                              <span className="text-[9px] font-bold print:hidden">TYHJÄ LUUKKU</span>
                              <span className="text-[8px] opacity-60 print:hidden">(saksataan pois apuviivan mukaan)</span>
                            </div>
                          );
                        }

                        const isSelected = item.id === selectedItemId;
                        
                        // Active and Non-active editing feedback styles
                        // Nonselected cards are dimmed down and slightly grayer to make the selected one pop out beautifully.
                        // The blue ring on the selected element is removed per request for a cleaner workspace appearance.
                        const dynamicHighlightClass = selectedItemId
                          ? (isSelected 
                              ? 'opacity-100 z-25 transition-all duration-300' 
                              : 'opacity-40 filter grayscale-[20%] brightness-[97%] hover:opacity-85 hover:grayscale-0 transition-opacity duration-300 print:opacity-100 print:filter-none')
                          : 'opacity-100';

                        return (
                          <div 
                            key={`tag-${pageIdx}-${item.id}-${itemIdx}`}
                            className={`no-break select-none cursor-pointer relative transition-all group/tag
                              ${dynamicHighlightClass}
                              preset-${config.preset} ${itemSpacingClass}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedItemId(item.id);
                            }}
                          >
                            {/* Highlight handle overlay on screen preview only */}
                            <div className="absolute top-2 left-2 no-print opacity-0 group-hover/tag:opacity-100 transition-opacity bg-[#005aa3] text-white font-sans text-[9px] px-2 py-0.5 rounded-xs cursor-pointer z-10 font-bold shadow">
                              Paina editoidaksesi
                            </div>
                            
                            <PriceTag item={item} config={config} />
                          </div>
                        );
                      })}
                    </div>

                    {/* Sheet-level Precise Cutting Lines Overlay (Continuous single-cuts) */}
                    {/* Rendered physically last in DOM so they always stack perfectly on top of A4 content */}
                    {config.showCutLines && (
                      <div className="absolute inset-0 pointer-events-none z-50">
                        {/* A5 PORTRAIT: One vertical cutting line down the center */}
                        {config.preset === 'A5_PORTRAIT' && (
                          <div className="absolute left-[148.5mm] top-0 bottom-0 w-[1.2px] bg-neutral-400 print:bg-neutral-800" />
                        )}

                        {/* A6 PORTRAIT: One vertical, one horizontal */}
                        {config.preset === 'A6_PORTRAIT' && (
                          <>
                            <div className="absolute left-[105mm] top-0 bottom-0 w-[1.2px] bg-neutral-400 print:bg-neutral-800" />
                            <div className="absolute top-[148.5mm] left-0 right-0 h-[1.2px] bg-neutral-400 print:bg-neutral-800" />
                          </>
                        )}

                        {/* A6 LANDSCAPE: One vertical, one horizontal */}
                        {config.preset === 'A6_LANDSCAPE' && (
                          <>
                            <div className="absolute left-[148.5mm] top-0 bottom-0 w-[1.2px] bg-neutral-400 print:bg-neutral-800" />
                            <div className="absolute top-[105mm] left-0 right-0 h-[1.2px] bg-neutral-400 print:bg-neutral-800" />
                          </>
                        )}

                        {/* SHOE_STRIP: Two vertical lines and 5 horizontal lines, perfectly centered */}
                        {config.preset === 'SHOE_STRIP' && (
                          <>
                            {/* 2 Vertical Lines at left (24mm) and right (273mm) edges of stack */}
                            <div 
                              className="absolute bg-neutral-400 print:bg-neutral-800 animate-fade-in" 
                              style={{ left: '24mm', top: '7mm', height: '196mm', width: '1.2px' }} 
                            />
                            <div 
                              className="absolute bg-neutral-400 print:bg-neutral-800 animate-fade-in" 
                              style={{ left: '273mm', top: '7mm', height: '196mm', width: '1.2px' }} 
                            />

                            {/* 5 Horizontal Lines (stretching 249mm from left-border 24mm to right-border 273mm) */}
                            <div className="absolute h-[1.2px] bg-neutral-400 print:bg-neutral-800" style={{ left: '24mm', width: '249mm', top: '7mm' }} />
                            <div className="absolute h-[1.2px] bg-neutral-400 print:bg-neutral-800" style={{ left: '24mm', width: '249mm', top: '56mm' }} />
                            <div className="absolute h-[1.2px] bg-neutral-400 print:bg-neutral-800" style={{ left: '24mm', width: '249mm', top: '105mm' }} />
                            <div className="absolute h-[1.2px] bg-neutral-400 print:bg-neutral-800" style={{ left: '24mm', width: '249mm', top: '154mm' }} />
                            <div className="absolute h-[1.2px] bg-neutral-400 print:bg-neutral-800" style={{ left: '24mm', width: '249mm', top: '203mm' }} />
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Quick info spacer */}
          <div className="h-4 no-print" />
        </section>
      </main>

      {/* No footer */}

      {/* Pop toast notifications (Hidden in print) */}
      <AnimatePresence>
        {showNotification && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            className="fixed bottom-12 left-1/2 -translate-x-1/2 bg-slate-900 text-white border border-slate-800 px-4 py-2.5 rounded shadow-xl z-50 flex items-center gap-2 text-xs font-semibold no-print"
          >
            <Check className="h-4 w-4 text-emerald-500 shrink-0" />
            <span>{showNotification}</span>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}
